// AdminAlertsView — admin liga/desliga emails de alerta sem deploy.
// [[project-admin-alerts-toggle]]
//
// Mostra: toggle ON/OFF + nota opcional + quem ligou por último + lista das
// 20 chaves de alerta mais recentes (origem/severidade/hits) pra ajudar a
// decidir quando ligar de novo.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  BellOff,
  CheckCircle2,
  Loader2,
  Save
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { api, ApiError } from "./api";
import { openAlert } from "./ConfirmPromptModal";

interface RecentAlert {
  key: string;
  source: string;
  summary: string;
  severity: "warning" | "critical";
  totalHits: number;
  lastSentAt: string;
}

interface ConfigState {
  enabled: boolean;
  note: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  updatedByEmail: string | null;
  recent: RecentAlert[];
}

const phase = signal<"loading" | "ready" | "error">("loading");
const state = signal<ConfigState | null>(null);
const errMsg = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.getAdminAlertsConfig();
    state.value = {
      enabled: r.enabled,
      note: r.note,
      updatedAt: r.updatedAt,
      updatedByName: r.updatedByName,
      updatedByEmail: r.updatedByEmail,
      recent: r.recent
    };
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function AdminAlertsView() {
  const [saving, setSaving] = useState(false);
  const [draftNote, setDraftNote] = useState("");

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (state.value) setDraftNote(state.value.note ?? "");
  }, [state.value?.note]);

  async function toggle(enabled: boolean) {
    if (!state.value) return;
    setSaving(true);
    try {
      await api.setAdminAlertsConfig({ enabled, note: draftNote || undefined });
      await load();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  async function saveNote() {
    if (!state.value) return;
    setSaving(true);
    try {
      await api.setAdminAlertsConfig({
        enabled: state.value.enabled,
        note: draftNote || undefined
      });
      await load();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  if (phase.value === "loading") {
    return (
      <div class="space-y-2">
        <Skeleton class="h-32 w-full" />
        <Skeleton class="h-48 w-full" />
      </div>
    );
  }
  if (phase.value === "error" || !state.value) {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  const s = state.value;

  return (
    <div class="space-y-6">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          {s.enabled ? (
            <Bell className="size-6 text-emerald-600" />
          ) : (
            <BellOff className="size-6 text-muted-foreground" />
          )}
          Alertas administrativos
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Emails automáticos pro admin quando algo crítico que afeta usuários
          dá pau (DB caiu, IA respondendo 5xx, gateway de pagamento fora,
          blob store indisponível). Liga/desliga global, sem deploy.
        </p>
      </header>

      {/* Toggle card */}
      <div
        class={`rounded-lg border p-6 transition-colors ${
          s.enabled
            ? "border-emerald-500/40 bg-emerald-500/5"
            : "border-zinc-500/40 bg-zinc-500/5"
        }`}
      >
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-3">
              <h2 class="text-lg font-semibold">
                {s.enabled ? "Ligados" : "Desligados"}
              </h2>
              {s.enabled ? (
                <span class="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="size-3" />
                  enviando emails
                </span>
              ) : (
                <span class="inline-flex items-center gap-1 rounded-full bg-zinc-500/15 px-2 py-0.5 text-xs font-semibold text-zinc-700 dark:text-zinc-300">
                  silenciados
                </span>
              )}
            </div>
            <p class="mt-2 text-sm text-muted-foreground">
              {s.enabled
                ? "Você vai receber email no luis.moraesfh@gmail.com quando incidentes ocorrerem. Dedupe ainda vale: warnings precisam 3+ hits pra emailar, criticals já no 1º."
                : "Nada é enviado. Os incidentes continuam acontecendo (logs/console) — só não chegam por email. Cache do toggle expira em 30s, então a mudança vira ativa rápido."}
            </p>
            {s.updatedAt && (
              <p class="mt-3 text-xs text-muted-foreground">
                Última mudança:{" "}
                <strong>
                  {new Date(s.updatedAt).toLocaleString("pt-BR")}
                </strong>{" "}
                por <strong>{s.updatedByName ?? s.updatedByEmail ?? "—"}</strong>
              </p>
            )}
          </div>
          <Switch
            checked={s.enabled}
            disabled={saving}
            onCheckedChange={(v) => void toggle(v)}
          />
        </div>

        <label class="mt-4 block">
          <span class="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Nota (opcional — fica registrado quem desligou e por quê)
          </span>
          <div class="flex items-center gap-2">
            <input
              type="text"
              value={draftNote}
              onInput={(e) => setDraftNote((e.target as HTMLInputElement).value)}
              placeholder="Ex: desligado por estar enviando muito email durante o stress test"
              class="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={() => void saveNote()}
              disabled={saving || draftNote === (s.note ?? "")}
              class="inline-flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              Salvar nota
            </button>
          </div>
        </label>
      </div>

      {/* Histórico recente de alertas */}
      <section>
        <h2 class="mb-3 text-base font-semibold">
          Últimos alertas registrados ({s.recent.length})
        </h2>
        {s.recent.length === 0 ? (
          <div class="rounded-md border border-dashed bg-muted/30 px-6 py-8 text-center text-sm text-muted-foreground">
            Nenhum alerta registrado ainda.
          </div>
        ) : (
          <div class="overflow-hidden rounded-md border">
            <table class="w-full text-sm">
              <thead class="bg-muted/40">
                <tr class="text-left">
                  <th class="px-3 py-2 font-medium">Severidade</th>
                  <th class="px-3 py-2 font-medium">Origem / chave</th>
                  <th class="px-3 py-2 font-medium">Resumo</th>
                  <th class="px-3 py-2 text-right font-medium">Hits</th>
                  <th class="px-3 py-2 font-medium">Última ocorrência</th>
                </tr>
              </thead>
              <tbody>
                {s.recent.map((r) => (
                  <tr key={r.key} class="border-t hover:bg-muted/30">
                    <td class="px-3 py-2">
                      {r.severity === "critical" ? (
                        <span class="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-400">
                          <AlertCircle className="size-3" />
                          critical
                        </span>
                      ) : (
                        <span class="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                          <AlertTriangle className="size-3" />
                          warning
                        </span>
                      )}
                    </td>
                    <td class="px-3 py-2">
                      <div class="font-medium">{r.source}</div>
                      <code class="text-[10px] text-muted-foreground">
                        {r.key}
                      </code>
                    </td>
                    <td class="px-3 py-2 text-muted-foreground">{r.summary}</td>
                    <td class="px-3 py-2 text-right tabular-nums font-semibold">
                      {r.totalHits}
                    </td>
                    <td class="px-3 py-2 text-muted-foreground">
                      {new Date(r.lastSentAt).toLocaleString("pt-BR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p class="mt-2 text-xs text-muted-foreground">
          Lista das 20 chaves mais recentes na tabela de dedupe. Cada chave
          agrega múltiplas ocorrências do MESMO tipo de erro.
        </p>
      </section>
    </div>
  );
}
